import { useState } from "react";
import { motion } from "motion/react";
import { KeyRound, MailCheck, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";

/**
 * 管理員登入
 * ═══════════════════════════════════════════════════════════════
 *
 * 兩步：Email + 密碼 → 收信箱裡的六位數。
 *
 * ── 舊版是三個欄位一次送出，理由是這個 ─────────────────────
 *
 * 「兩階段會產生一個『密碼正確但還沒過驗證碼』的中間狀態，
 *   攻擊者可以用它來驗證帳密組合是否正確 —— 即使他沒有驗證碼。」
 *
 * 那個理由沒有失效。二步驗證改成電子郵件之後兩階段躲不掉
 * （碼要先寄出去），所以那個訊號是用另一種方式補回來的：
 *
 * **伺服器對錯誤的帳密也回 two_factor_required，附一個假的挑戰。**
 *
 * 帳號不存在、密碼錯、帳號停用，三種情況得到的回應與成功時
 * 一模一樣 —— 包括遮罩過的信箱。攻擊者會走到第二步，
 * 然後拿到「驗證碼不正確」，跟真的打錯一個字沒有差別。
 * 見 services/identity 的 /admin/auth/login。
 *
 * 能看到這個畫面，代表你的 IP 已經通過白名單。
 */
export default function Login({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");

  /*
    challenge 只放在 state 裡：不進網址（會留在瀏覽器歷史與 Referer）、
    不進 localStorage（XSS 就拿得到）。它十分鐘後失效，
    而且離開這個分頁就沒了 —— 那正是我們要的。
  */
  const [challenge, setChallenge] = useState<string | null>(null);
  const [hint, setHint] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email, password }),
      });
      const payload = await res.json().catch(() => null);

      /*
        two_factor_required 不是失敗，是流程還沒完成。
        伺服器對錯誤的帳密也回這個，所以這裡不能、也沒辦法分辨 ——
        那正是設計要的。
      */
      if (payload?.error?.code === "two_factor_required") {
        setChallenge(String(payload.error.details?.challenge ?? ""));
        setHint(String(payload.error.details?.emailHint ?? ""));
        return;
      }

      if (!res.ok) {
        setError(res.status === 429 ? t("login.tooMany") : t("login.fail"));
        return;
      }
      onSuccess();
    } catch {
      setError(t("login.network"));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/2fa", {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ challenge, code }),
      });
      if (!res.ok) {
        setError(t("login.badCode"));
        setCode("");
        return;
      }
      onSuccess();
    } catch {
      setError(t("login.network"));
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "h-11 w-full rounded-2xl nm-inset px-4 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus:shadow-[inset_2px_2px_5px_var(--nm-shadow),inset_-2px_-2px_5px_var(--nm-light),0_0_0_2px_var(--color-brand-500)]";

  return (
    <div className="grid min-h-screen place-items-center bg-page px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm rounded-card nm p-8"
      >
        <div className="flex items-center gap-2.5">
          <span className="flex size-10 items-center justify-center rounded-2xl nm-inset text-brand-400">
            {challenge ? <MailCheck className="size-5" /> : <ShieldCheck className="size-5" />}
          </span>
          <div>
            <p className="text-sm font-semibold text-ink">{t("shell.brand")}</p>
            <p className="text-[11px] text-ink-muted">
              {challenge ? t("login.codeSentTo", { hint }) : t("login.allowlisted")}
            </p>
          </div>
        </div>

        {challenge ? (
          <form onSubmit={submitCode} className="mt-8 space-y-5">
            <div className="space-y-1.5">
              <label htmlFor="code" className="text-sm font-medium text-ink">
                {t("login.code")}
              </label>
              <input
                id="code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                className="h-11 w-full rounded-2xl nm-inset px-4 text-center font-mono text-lg tracking-[0.4em] text-ink placeholder:tracking-[0.4em] placeholder:text-ink-muted focus:outline-none focus:shadow-[inset_2px_2px_5px_var(--nm-shadow),inset_-2px_-2px_5px_var(--nm-light),0_0_0_2px_var(--color-brand-500)]"
              />
              {/*
                「沒收到信」在這個設計下有兩種可能，而伺服器不會告訴你
                是哪一種 —— 錯誤的帳密也會走到這一步（見檔頭的說明）。

                所以這句話要主動講。不講的話，一個密碼打錯一個字的人
                會坐在這裡等一封永遠不會來的信，而畫面上每一個字都在
                告訴他信已經寄出去了。

                這句話對攻擊者沒有洩漏任何東西：它對每一個人都一樣。
              */}
              <p className="text-[11px] leading-relaxed text-ink-muted">
                {t("login.codeTtl")}
                <br />
                {t("login.codeHint")}
              </p>
            </div>

            {error && (
              <p className="rounded-2xl nm-inset px-4 py-3 text-sm text-brand-300" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || code.length < 6}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-2xl nm-pressable text-sm font-semibold text-ink disabled:opacity-40"
            >
              <KeyRound className="size-4" />
              {t("login.submit")}
            </button>

            <button
              type="button"
              onClick={() => {
                setChallenge(null);
                setCode("");
                setError(null);
              }}
              className="w-full text-center text-[11px] text-ink-muted underline-offset-4 hover:underline"
            >
              {t("login.backToPassword")}
            </button>
          </form>
        ) : (
          <form onSubmit={submitPassword} className="mt-8 space-y-5">
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium text-ink">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-medium text-ink">
                {t("login.password")}
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
              />
              <p className="text-[11px] text-ink-muted">
                {t("login.lockoutNote")}
              </p>
            </div>

            {error && (
              <p className="rounded-2xl nm-inset px-4 py-3 text-sm text-brand-300" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-2xl nm-pressable text-sm font-semibold text-ink disabled:opacity-40"
            >
              <KeyRound className="size-4" />
              {t("login.next")}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
